'use client';

import { useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import Markdown from '@/app/common/Markdown';
import { ChunkAST, FootnoteDefinition } from '@/lib/parseMarkdown';
import { type PlanId } from '@/lib/branchTypes';
import type { PlanAPhaseData } from '@/data/plan-a/planATypes';
import type { Globe2Settings } from '@/app/playbook/globe/globe2Types';
import PrintCover from './PrintCover';
import PrintBackCover from './PrintBackCover';
import Appendices from './Appendices';
import { PrintSidePanel } from './PrintSidePanel';
import { makePrintSidenote } from './printSidenote';
import { extractDetailsToAppendices } from './detailsToAppendices';
import { unwrapInteractiveDirectives } from './unwrapDirectives';
import { injectGraphSlots, stripDateMarkers } from './hudPanel';
import { bookPageStyle, runningHeaderStyle } from './runningHeader';
import { PrintTextPrep } from './PrintTextPrep';
import { PrintGutterAlign } from './printGutterAlign';
import { PrintTableSidenotes } from './PrintTableSidenotes';
import './print.css';

export interface PrintDoc {
  slug: string;
  title: string;
  author?: string;
  nodes: ChunkAST[];
}

// How the part dividers name the main plan (kickers, "The scenario above follows…").
const PLAN_DIVIDER_LABELS: Record<string, string> = {
  A: 'Plan A',
  B: 'Plan B',
  C: 'Plan C',
  D: 'Plan D',
  S: 'Plan S',
};

// Renders one document's body + its own (per-document) appendices. Footnotes and
// the details→appendix lettering are scoped to the document, so each supplement
// numbers its own notes and carries its own "Appendix A…" set right after it.
function DocBody({
  doc,
  graphMaxYear,
  appendicesDivider,
  dualGutterPanels = false,
}: {
  doc: PrintDoc;
  // When set (the main scenario only), inject a chapter side panel at each
  // year's dateMarker up to and including this year (the data range).
  graphMaxYear?: number;
  // Optional full-page part divider opening this document's appendices
  // (replaces the compact "Appendices — …" heading).
  appendicesDivider?: ReactNode;
  // Bound-book layout: emit a left-gutter copy of each side panel too.
  dualGutterPanels?: boolean;
}) {
  const footnotes: FootnoteDefinition[] =
    doc.nodes.length > 0 ? doc.nodes[0].docContext.footnotes : [];
  const Sidenote = makePrintSidenote(footnotes);
  let flat = doc.nodes.flatMap((chunk) => chunk.nodes);
  flat =
    graphMaxYear != null
      ? injectGraphSlots(flat, graphMaxYear, dualGutterPanels)
      : stripDateMarkers(flat);
  const { nodes, appendices } = extractDetailsToAppendices(
    unwrapInteractiveDirectives(flat),
  );
  return (
    <>
      <div className="markdown-content print-prose">
        <Markdown nodes={nodes} FootnoteHandler={Sidenote} />
      </div>
      <Appendices
        appendices={appendices}
        footnotes={footnotes}
        heading={`Appendices — ${doc.title}`}
        divider={appendicesDivider}
      />
    </>
  );
}

/**
 * The full AI-2040 book: cover, the linearized main scenario (preamble + the
 * chosen plan ending), then the supplements as back-matter — each supplement a
 * chapter with its own sidenotes and appendices, using the exact same magazine
 * handling as the standalone supplement PDFs.
 */
export default function PrintScenario({
  scenario,
  altPlans = [],
  supplements,
  coverTitle,
  coverSubtitle,
  plan,
  playbookData,
  globeSettings,
  bookMargins = false,
}: {
  scenario: PrintDoc;
  // The other plans (?altplans=1), each a chapter after the scenario.
  altPlans?: PrintDoc[];
  supplements: PrintDoc[];
  coverTitle: string;
  coverSubtitle?: string;
  plan: PlanId;
  playbookData: PlanAPhaseData[];
  globeSettings?: Globe2Settings;
  // Bound-book layout (?book=1): alternating sidenote gutters via dual note
  // copies + negative spine-side page margins — one render, CMD+P-printable
  // (print.css "Bound-book layout", runningHeader.tsx bookPageStyle).
  bookMargins?: boolean;
}) {
  // Mount the real PlaybookExtrapolatedGraph into each chapter slot, frozen to
  // that chapter's date. It's a client component with a canvas layer, so it has
  // to render in the browser — we mount one React root per slot (the slots are
  // raw DOM inside the markdown's dangerouslySetInnerHTML, so separate roots are
  // safe). renderRoute waits for these to paint before printing.
  useEffect(() => {
    const slots = document.querySelectorAll<HTMLElement>(
      '.print-graph-slot[data-graph-date]',
    );
    for (const slot of Array.from(slots)) {
      // Idempotent: a `mounted` flag means StrictMode's double-invoke (and any
      // re-run) won't createRoot the same node twice. No cleanup/unmount — this
      // is a render-once print page, and unmounting on the StrictMode pass would
      // blank graphs the skipped second pass never re-mounts.
      if (slot.dataset.graphMounted) continue;
      const date = slot.getAttribute('data-graph-date');
      if (!date) continue;
      slot.dataset.graphMounted = '1';
      createRoot(slot).render(
        <PrintSidePanel
          date={new Date(date)}
          plan={plan}
          playbookData={playbookData}
          globeSettings={globeSettings}
          /* The hero treatment (capability graph + dither gradient + globe on
             the first chapter) is off for now — every chapter renders just
             the changing data block. PrintSidePanel keeps the machinery for
             when the hero design comes back. */
        />,
      );
    }
  }, [plan, playbookData, globeSettings]);

  return (
    <div className={bookMargins ? 'print-root print-book' : 'print-root'}>
      <PrintTextPrep />
      {/* Before PrintGutterAlign: effects run in mount order, and the notes
          must leave their table cells before the aligner measures the floats
          (it hides copies still trapped in cells). */}
      <PrintTableSidenotes />
      {bookMargins ? <PrintGutterAlign /> : null}
      {runningHeaderStyle(coverTitle)}
      {bookMargins ? bookPageStyle(coverTitle) : null}

      <PrintCover
        title={coverTitle}
        subtitle={coverSubtitle}
        kicker="THE AI 2040 SCENARIO"
        publishLine={'A plan for the development of\nsuperhuman AI · ai-2040.com'}
      />

      {/* The back of the front cover (?book=1 only): a deliberately blank
          verso so nothing prints on the reverse of the cover stock, and the
          scenario opens on a recto. The named page's margin-box clears ride
          along here (after runningHeaderStyle) because Chrome resolves
          same-name @page margin boxes by stylesheet order, not page-selector
          specificity. Screen-read PDFs skip it — with no physical page turn,
          a blank page 2 is just a scroll bump. */}
      {bookMargins ? (
        <section className="print-cover-verso" aria-hidden="true">
          <style
            dangerouslySetInnerHTML={{
              __html: `@page cover-verso {
                @top-left { content: ""; }
                @top-right { content: ""; }
                @bottom-center { content: ""; }
                @bottom-left { content: ""; }
                @bottom-right { content: ""; }
              }`,
            }}
          />
        </section>
      ) : null}

      <article className="print-essay">
        {/* The main scenario. Its appendices open with a full-page part
            divider (the supplements' per-chapter appendix sets keep their
            compact headings). */}
        <DocBody
          doc={scenario}
          dualGutterPanels={bookMargins}
          graphMaxYear={Math.max(
            ...playbookData.map((p) => Number(p.date.slice(0, 4))),
          )}
          appendicesDivider={
            <section className="print-part-divider">
              <div className="print-part-divider-kicker">
                AI 2040: {PLAN_DIVIDER_LABELS[plan] ?? plan}
              </div>
              <h1>Appendices</h1>
              <p>
                The scenario&rsquo;s deep-dive boxes, collected here as
                lettered appendices. Each is cross-referenced from the point
                in the text it expands on.
              </p>
            </section>
          }
        />

        {/* The other plans as chapters (?altplans=1). */}
        {altPlans.length > 0 ? (
          <>
            <section className="print-part-divider">
              <div className="print-part-divider-kicker">
                AI 2040: {PLAN_DIVIDER_LABELS[plan] ?? plan}
              </div>
              <h1>The Alternative Plans</h1>
              <p>
                The scenario above follows {PLAN_DIVIDER_LABELS[plan] ?? plan}.
                These chapters show how each of the other plans plays out
                from the same branch point.
              </p>
            </section>
            {altPlans.map((planDoc) => (
              <section key={planDoc.slug} className="print-supplement">
                <header className="print-supplement-header">
                  <h1 className="print-supplement-title">{planDoc.title}</h1>
                </header>
                <DocBody doc={planDoc} />
              </section>
            ))}
          </>
        ) : null}

        {/* Supplements as back-matter. */}
        {supplements.length > 0 ? (
          <>
            <section className="print-part-divider">
              <div className="print-part-divider-kicker">
                AI 2040: {PLAN_DIVIDER_LABELS[plan] ?? plan}
              </div>
              <h1>Supplements</h1>
              <p>
                Deep-dives on specific parts of Plan A. Each is self-contained.
              </p>
            </section>
            {supplements.map((supp) => (
              <section key={supp.slug} className="print-supplement">
                <header className="print-supplement-header">
                  <h1 className="print-supplement-title">{supp.title}</h1>
                  {supp.author ? (
                    <p className="print-supplement-author">{supp.author}</p>
                  ) : null}
                </header>
                <DocBody doc={supp} />
              </section>
            ))}
          </>
        ) : null}
      </article>

      <PrintBackCover
        scenarioLabel={coverSubtitle}
        altPlanLabels={altPlans.map((planDoc) => planDoc.title)}
        includesSupplements={supplements.length > 0}
      />
    </div>
  );
}
